import { useEffect, useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { fetchCNYPurposeCodes } from "../../../redux/slice";

export interface PurposeCodeItem {
  id?: number | string;
  code: string;
  description: string;
  createdAt?: string;
  updatedAt?: string;
}

export const usePurposeCodeLookup = () => {
  const dispatch = useAppDispatch();
  const { keys } = useAppSelector((state) => state.settings);

  const [purposeCodes, setPurposeCodes] = useState<PurposeCodeItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const publicKey = keys?.data?.publicKey;

  useEffect(() => {
    let isMounted = true;

    const loadPurposeCodes = async () => {
      if (!publicKey) return;
      setLoading(true);

      try {
        const action = await dispatch(fetchCNYPurposeCodes(publicKey));
        if (fetchCNYPurposeCodes.fulfilled.match(action)) {
          const rawData = action.payload?.data || action.payload || [];
          const list: PurposeCodeItem[] = Array.isArray(rawData) ? rawData : [];
          if (isMounted) {
            setPurposeCodes(list);
            setError(null);
          }
        } else if (fetchCNYPurposeCodes.rejected.match(action)) {
          if (isMounted) {
            setError((action.payload as string) || "Failed to load purpose codes");
          }
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message || "An error occurred fetching purpose codes");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPurposeCodes();

    return () => {
      isMounted = false;
    };
  }, [dispatch, publicKey]);

  const getPurposeCodeByDescription = useCallback(
    (description?: string): string => {
      if (!description) return "PYR001";

      if (description.startsWith("PYR")) {
        return description;
      }

      const normalizedInput = description.trim().toLowerCase();

      const exactMatch = purposeCodes.find(
        (item) => item.description?.trim().toLowerCase() === normalizedInput
      );
      if (exactMatch?.code) {
        return exactMatch.code;
      }

      const partialMatch = purposeCodes.find((item) => {
        const itemDesc = item.description?.toLowerCase() || "";
        return itemDesc.includes(normalizedInput) || normalizedInput.includes(itemDesc);
      });
      if (partialMatch?.code) {
        return partialMatch.code;
      }

      if (normalizedInput.includes("service")) return "PYR001";
      if (normalizedInput.includes("advance") && normalizedInput.includes("good")) return "PYR031";
      if (normalizedInput.includes("good")) return "PYR003";
      if (normalizedInput.includes("medical")) return "PYR026";
      if (normalizedInput.includes("vendor") || normalizedInput.includes("software")) return "PYR032";
      if (normalizedInput.includes("education") || normalizedInput.includes("student")) return "PYR025";

      return "PYR001";
    },
    [purposeCodes]
  );

  return {
    purposeCodes,
    loading,
    error,
    getPurposeCodeByDescription,
  };
};
